import { ApplicationConfig, importProvidersFrom, inject, Injectable, provideAppInitializer } from '@angular/core';
import {
	AlarisApiService,
	AlarisConfigService,
	AlarisDateFormatService,
	AlarisErrorInterceptor,
	AlarisLanguageService,
	AlarisLocalStorageService,
	AUTH_SERVICE_INJECTOR,
	China,
	CONFIG_SERVICE_INJECTOR,
	DATE_FILTER_END_OF_THE_DAY_INJECTOR,
	DATE_FORMAT_SERVICE_INJECTOR,
	EDIT_PANEL_CONFIG,
	English,
	ENVIRONMENT,
	FILES_SERVICE_INJECTOR,
	ICONS_CONFIG,
	IconsConfig,
	LANGUAGE_CONFIG,
	LANGUAGE_SERVICE_INJECTOR,
	LanguageConfig,
	LOCALSTORAGE_SERVICE_INJECTOR,
	MENU_MQ_SERVICE_INJECTOR,
	MENU_MQ_WIDTH_VALUE,
	MenuSidebarService,
	PROFILE_CONFIG,
	PROFILE_SERVICE_INJECTOR,
	ProfileConfig,
	provideAlarisToast,
	Russian,
	Spain,
	TAGGABLE_FIELD_CONFIG,
} from '@campaign-portal/components-library';
import { environment } from '../environments/environment';
import {
	HTTP_INTERCEPTORS,
	HttpBackend,
	HttpClient,
	provideHttpClient,
	withInterceptorsFromDi,
} from '@angular/common/http';
import { ImportRatesFileService } from './pages/import rates/import-rates-dialog/import-rates-file.service';
import { AuthService } from './auth/auth.service';
import { UserService } from './auth/user.service';
import { RpcInterceptor } from '@helpers/interceptors/rpc.interceptor';
import { ExportConfig } from '@helpers/interceptors/export-config';
import { AuthGuardService } from './auth/auth.guard';
import { DropAuthGuardService } from './auth/drop-auth.guard';
import { DatePipe, registerLocaleData } from '@angular/common';
import { OVERLAY_DEFAULT_CONFIG, OverlayDefaultConfig } from '@angular/cdk/overlay';
import {
	MissingTranslationHandler,
	MissingTranslationHandlerParams,
	TranslateDefaultParser,
	TranslateLoader,
	TranslateModule,
	TranslateParser,
} from '@ngx-translate/core';
import { MultiTranslateHttpLoader } from 'ngx-translate-multi-http-loader';
import { from, mergeMap, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { STORAGE } from '@helpers/types/consts';
import { provideRouter } from '@angular/router';
import { APP_ROUTES } from './app.routing';
import { provideAnimations } from '@angular/platform-browser/animations';
import { HlrSettingsStartupParams } from '@helpers/types/hlr';
import { USERS_AND_ROLES_API, UsersAndRolesAPI } from '@helpers/shared/injection-tokens';
import { HlrConfigService } from '@helpers/providers/hlr-config.service';

import localeEn from '@angular/common/locales/en';
import localeEnExtra from '@angular/common/locales/extra/en';
import localeEs from '@angular/common/locales/es';
import localeEsExtra from '@angular/common/locales/extra/es';
import localeRu from '@angular/common/locales/ru';
import localeRuExtra from '@angular/common/locales/extra/ru';
import { DS_ICONS_CONFIG } from '@alarislabs/angular-components/icon-swap';
import { ResponseInterceptor } from '@helpers/interceptors/response.interceptor';

/* Translate Module setup */
function HttpLoaderFactory(http: HttpBackend): MultiTranslateHttpLoader {
	return new MultiTranslateHttpLoader(http, [
		{ prefix: './assets/i18n_lib/app.', suffix: '.json?' + environment.version },
		{ prefix: './assets/i18n/app.', suffix: '.json?' + environment.version },
	]);
}

class MissingTranslationService implements MissingTranslationHandler {
	handle(params: MissingTranslationHandlerParams): string {
		console.warn(`WARN: '${params.key}' is missing in '${params.translateService.currentLang}' locale`);
		return params.key;
	}
}

@Injectable()
class TranslateParserService extends TranslateDefaultParser {
	constructor() {
		super();
	}

	override getValue(target: Record<string, unknown>, key: string): string {
		const result = super.getValue(target, key);
		return typeof result === 'object' ? key : result;
	}
}

const translateConfig = {
	loader: {
		provide: TranslateLoader,
		useFactory: HttpLoaderFactory,
		deps: [HttpBackend],
	},
	missingTranslationHandler: {
		provide: MissingTranslationHandler,
		useClass: MissingTranslationService,
	},
	parser: { provide: TranslateParser, useClass: TranslateParserService },
	// if we set `defaultLanguage` to 'en' right here - we force loading en.json with translations
	// so missing translations will be used from en.file
	defaultLanguage: '',
};

/* End of Translate Module setup */

/* Config */
const profileConfig: ProfileConfig = {
	profileRoute: '/profile',
	termsUrl: '',
	privacyUrl: '',
	supportEmail: '',
};

const userAndRolesApi: UsersAndRolesAPI = {
	url: '',
};

const iconsConfig: IconsConfig = {
	baseUrl: '/assets/icons',
};

const languageConfig: LanguageConfig = {
	languages: [English, Spain, Russian, China],
	currentLanguage: English,
};

export function loadConfig(): Observable<void> {
	const appConfig = inject(HlrConfigService);
	const store = inject(AlarisLocalStorageService);
	const translate = inject(AlarisLanguageService);
	const http = inject(HttpClient);

	return from(appConfig.load()).pipe(
		mergeMap(() => {
			const reqTo = 'HlrSettings.ReadStartupParams';
			return http
				.post<HlrSettingsStartupParams>(appConfig.api + '?' + reqTo, {
					method: reqTo,
				})
				.pipe(
					map((res) => {
						const lang = store.get(STORAGE.LANGUAGE) ?? 'en';
						translateConfig.defaultLanguage = lang;
						registerLocaleData(localeEn, English.id, localeEnExtra);
						registerLocaleData(localeEs, Spain.id, localeEsExtra);
						registerLocaleData(localeRu, Russian.id, localeRuExtra);
						translate.set(lang);

						profileConfig.termsUrl = appConfig.termsUrl;
						profileConfig.privacyUrl = appConfig.privacyUrl;
						profileConfig.supportEmail = appConfig.supportEmail;

						userAndRolesApi.url = appConfig.api + 'usersroles/';
						iconsConfig.baseUrl = appConfig.env.iconsBaseUlr ?? '/assets/icons';

						if (!appConfig?.isCore && !res.data?.enableAddLang) {
							languageConfig.languages = [English, Spain, China];
						}
						appConfig.eapiEnable = res.data.eapiEnable;

						environment.version = res.data.version + ' ' + res.data.release;
						if (res.data.buildDate) {
							const date = new Date(res.data.buildDate);

							const options: Intl.DateTimeFormatOptions = {
								day: 'numeric',
								month: 'long',
								year: 'numeric',
							};

							environment.releaseDate = new Intl.DateTimeFormat('en-US', options).format(date).toLowerCase();
						}
					})
				);
		})
	);
}

export const APP_CONFIG: ApplicationConfig = {
	providers: [
		importProvidersFrom(TranslateModule.forRoot(translateConfig)),
		provideAlarisToast({ tapToDismiss: false, timeOut: 7000 }),
		provideAnimations(),
		provideRouter(APP_ROUTES),
		provideHttpClient(withInterceptorsFromDi()),
		provideAppInitializer(loadConfig),

		AlarisConfigService<{ iconsBaseUlr?: string }>,
		AlarisLocalStorageService,
		AlarisLanguageService,
		AlarisApiService,
		HlrConfigService,

		{ provide: ENVIRONMENT, useValue: environment },
		ImportRatesFileService,
		{ provide: FILES_SERVICE_INJECTOR, useExisting: ImportRatesFileService },

		{ provide: LANGUAGE_CONFIG, useValue: languageConfig },

		{ provide: DS_ICONS_CONFIG, useValue: iconsConfig },
		{ provide: ICONS_CONFIG, useValue: iconsConfig },
		{ provide: PROFILE_CONFIG, useValue: profileConfig },

		{ provide: CONFIG_SERVICE_INJECTOR, useExisting: HlrConfigService },
		{ provide: LOCALSTORAGE_SERVICE_INJECTOR, useExisting: AlarisLocalStorageService },
		{ provide: LANGUAGE_SERVICE_INJECTOR, useExisting: AlarisLanguageService },
		{ provide: AUTH_SERVICE_INJECTOR, useExisting: AuthService },
		{ provide: PROFILE_SERVICE_INJECTOR, useExisting: UserService },

		{ provide: DATE_FILTER_END_OF_THE_DAY_INJECTOR, useValue: true },

		{ provide: HTTP_INTERCEPTORS, useClass: RpcInterceptor, multi: true },

		{ provide: HTTP_INTERCEPTORS, useClass: ResponseInterceptor, multi: true },
		{ provide: HTTP_INTERCEPTORS, useClass: AlarisErrorInterceptor, multi: true },

		{ provide: HTTP_INTERCEPTORS, useClass: ExportConfig, multi: true },

		{ provide: MENU_MQ_SERVICE_INJECTOR, useClass: MenuSidebarService },
		{ provide: MENU_MQ_WIDTH_VALUE, useValue: '(max-width: 1365px)' },

		{ provide: USERS_AND_ROLES_API, useValue: userAndRolesApi },
		AuthGuardService,
		DropAuthGuardService,
		{ provide: TAGGABLE_FIELD_CONFIG, useValue: { translation: false } },
		{ provide: EDIT_PANEL_CONFIG, useValue: { closeOnBackDropClick: false } },
		{ provide: DATE_FORMAT_SERVICE_INJECTOR, useClass: AlarisDateFormatService },
		DatePipe,
		{ provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } satisfies OverlayDefaultConfig }
	],
};
